import { getDeviceType, getWindowWidth } from "@/common/utils/device";
import { apiMetrics } from "@/common/services";
import { useGeolocationStore } from "@/common/store/geolocationStore";

const loader = () => import("web-vitals");

const ENABLED_COUNTRIES = ["US", "UK", "DE"];

const WHITE_LIST_PAGE_GROUPS = [
  "home", "ifr", "search", "users", "watch", "create", "auth",
  "niches", "explore", "categories", "settings", "dashboard",
  "followers", "creators", "faq", "following", "user",
  "profile", "unsubscribed", "signup", "search-warning", "authorize", "app",
];

interface IMetric {
  name: string;
  delta: number;
}

interface ISendMetricsParams {
  pageGroup: string;
  deviceType: string;
  experiments: string[];
}

type IMetricsData = {
  lcp?: number;
  fcp?: number;
  cls?: number;
  inp?: number;
  ttfb?: number;
}

const defaultMetricsData: IMetricsData = {
  lcp: undefined,
  fcp: undefined,
  cls: undefined,
  inp: undefined,
  ttfb: undefined,
};

let metricsData: IMetricsData = { ...defaultMetricsData };

const getPageGroup = (pathname: string) => {
  if (pathname === "/") {
    return "home";
  }

  return pathname.split("/")[1];
};

const initialize = async () => {
  if (!import.meta.env.REACT_APP_WEB_VITALS_SAMPLE_RATE) {
    return;
  }

  if (Math.random() > Number(import.meta.env.REACT_APP_WEB_VITALS_SAMPLE_RATE)) {
    return;
  }

  const pageGroup = getPageGroup(window.location.pathname);

  if (!WHITE_LIST_PAGE_GROUPS.includes(pageGroup)) {
    return;
  }

  if (import.meta.env.REACT_APP_WEB_VITALS_ENABLE !== "1") {
    return;
  }

  const { onLCP, onCLS, onINP, onTTFB, onFCP } = await loader();

  const params = {
    pageGroup,
    deviceType: getDeviceType(getWindowWidth()),
    experiments: [],
  };

  onCLS(saveMetric);
  onLCP(saveMetric);
  onINP(saveMetric);
  onTTFB(saveMetric);
  onFCP(saveMetric);

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendMetrics(metricsData, params);
      metricsData = { ...defaultMetricsData };
    }
  });

  console.debug("Web Vitals initialized.");
};

const saveMetric = (metric: IMetric) => {
  const name = (metric.name.toLowerCase()) as keyof IMetricsData;
  metricsData[name] = metric.delta;
};

const sendMetrics = (metrics: IMetricsData, params: ISendMetricsParams) => {
  const validMetrics = filterMetrics(metrics);

  if (Object.keys(validMetrics).length === 0) {
    return;
  }

  const country = useGeolocationStore.getState().location?.country ?? "";

  if (import.meta.env.REACT_APP_WEB_VITALS_FILTER_BY_COUNTRIES === "1" && !ENABLED_COUNTRIES.includes(country)) {
    return;
  }

  const payload = {
    ...metrics,
    page_group: params.pageGroup,
    device_type: params.deviceType,
    experiments: params.experiments,
  };

  return apiMetrics.sendWebVitalsMetrics(payload);
};

const filterMetrics = (metrics: IMetricsData) => {
  const validMetrics: Partial<IMetricsData> = {};

  for (const prop in metrics) {
    const key = prop as keyof IMetricsData;
    const value = metrics[key];

    if (value !== undefined && value >= 0) {
      validMetrics[key] = metrics[key];
    }
  }

  return validMetrics;
};

export const startWebVitals = () => {
  try {
    initialize();
  } catch (e) {
    console.error(`Error initializing Web Vitals: ${e}`);
  }
};
